import {
  Snackbar,
  Alert,
  CircularProgress,
  Backdrop,
  Icon,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Grid,
  Table,
  TableRow,
  TableCell,
} from "@mui/material";

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import usePageTitle from "app/pageTitle";

import DataTable from "theme/Tables/DataTable";

function AgentBenefitViewPage() {
  const title = "Benefit";
  usePageTitle(title);

  const { ref } = useParams();
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [benefit, setBenefit] = useState({});
  const [groups, setGroups] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);
  const [spend, setSpend] = useState(0);
  const [currency, setCurrency] = useState("₦");
  const [vat, setVat] = useState(0.075);
  const [serviceFee, setServiceFee] = useState(0.025);

  const [selectedAgentGroup, setSelectedAgentGroup] = useState(null);
  const [groupbenefit, setGroupbenefit] = useState(null);
  const [openNewAgentDialog, setOpenNewAgentDialog] = useState(false);

  const [fundingAmount, setFundingAmount] = useState(null);
  const [vatCharge, setVatCharge] = useState(0);
  const [serviceCharge, setServiceCharge] = useState(0);
  const [totalCharge, setTotalCharge] = useState(0);
  const [openFundingDialog, setOpenFundingDialog] = useState(false);
  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);

  async function deleteGroup(reference) {
    setShowLoading(true);
    await axios
      .get(`portal/agent/benefits/groups/${reference}/delete.json`)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const groupsTableColumns = [
    { Header: "Group", accessor: "group", align: "left" },
    { Header: "Benefit", accessor: "benefit", align: "left" },
    { Header: "Actions", accessor: "action", align: "right" },
  ];

  const buildGroupsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        group: row.title,
        benefit: `${currency} ${(row.benefit / 100).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        action: (
          <MDBox mt={1} variant="button">
            <Tooltip title="Delete">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  if (window.confirm(`Remove ${row.title}?`) === true) {
                    deleteGroup(row.reference);
                  }
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                  delete
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    if (fundingAmount === null || fundingAmount === 0) {
      setServiceCharge(0);
      setVatCharge(0);
      setTotalCharge(0);
      return;
    }

    let sFee = serviceFee.fee / 100;
    if (serviceFee.is_percentage === true) {
      sFee = parseFloat(fundingAmount) * serviceFee.fee;
      if (serviceFee.minimum / 100 > sFee && serviceFee.minimum !== 0) {
        sFee = serviceFee.minimum / 100;
      }
      if (serviceFee.maximum !== 0 && serviceFee.maximum / 100 < sFee) {
        sFee = serviceFee.maximum / 100;
      }

      if (serviceFee.mandatory) {
        sFee += serviceFee.mandatory;
      }
    }

    let vFee = sFee * vat;
    setServiceCharge(sFee);
    setVatCharge(vFee);
    setTotalCharge(sFee + parseFloat(fundingAmount) + vFee);
  }, [fundingAmount]);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`portal/agent/benefits/${ref}/fetch.json`)
        .then((result) => {
          setShowLoading(false);
          // console.log(result.data.data);
          if (result.data.status === "success") {
            setBenefit(result.data.data.benefit);
            setSpend(result.data.data.spend);
            setVat(result.data.data.vat);
            setServiceFee(result.data.data.serviceFee);

            setAgentGroups(result.data.data.agent_groups);

            const rowsG = buildGroupsRows(result.data.data.groups);
            setGroups(rowsG);

            setCurrency(result.data.data.currency);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function assignGroup() {
    if (selectedAgentGroup === null) {
      setSeverity("error");
      setErrMsg("An agent group is required.");
      setOpen(true);
      return;
    }

    if (groupbenefit === null || parseFloat(groupbenefit, 10) <= 0) {
      setSeverity("error");
      setErrMsg("invalid benefit amount.");
      setOpen(true);
      return;
    }

    setOpenNewAgentDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      group: selectedAgentGroup,
      benefit: parseFloat(groupbenefit, 10) * 100,
    };

    await axios
      .post("portal/agent/benefits/groups/assign.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteBenefit() {
    setShowLoading(true);
    await axios
      .get(`/portal/agent/benefits/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function updateStatus() {
    setShowLoading(true);
    await axios
      .get(`/portal/agent/benefits/${ref}/status.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function fundBalance() {
    if (!fundingAmount) {
      setSeverity("error");
      setErrMsg("Amount is invalid.");
      setOpen(true);
      return;
    }

    if (window.confirm("Your wallet will be debited instantly. Proceed?") === false) {
      return;
    }

    setOpenFundingDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      amount: parseFloat(fundingAmount, 10) * 100,
    };

    await axios
      .post("portal/agent/benefits/fund.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function withdrawBalance() {
    if (!fundingAmount) {
      setSeverity("error");
      setErrMsg("Amount is invalid.");
      setOpen(true);
      return;
    }

    setOpenWithdrawDialog(false);
    setShowLoading(true);
    const json = {
      reference: ref,
      amount: parseFloat(fundingAmount, 10) * 100,
    };

    await axios
      .post("portal/agent/benefits/withdraw.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewAgentDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Assign Agent Group</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Agent Group</InputLabel>
                <Select
                  label="Agent Group"
                  onChange={(e) => {
                    setSelectedAgentGroup(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    agentGroups.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      agentGroups.map((record) => (
                        <MenuItem value={`${record.reference}`}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                name="title"
                margin="dense"
                label={`${benefit.benefit_type} Amount`}
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setGroupbenefit(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewAgentDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              assignGroup();
            }}
          >
            Add
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openFundingDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Fund Balance</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Amount"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setFundingAmount(e.target.value)}
              />
            </MDBox>
            <MDBox mt={4}>
              <Table>
                <TableRow>
                  <TableCell>
                    <MDTypography fontSize={12}>Amount</MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography align="right" fontSize={12}>
                      {`${currency}${parseInt(fundingAmount ? fundingAmount : 0, 10).toLocaleString(
                        "en",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )}`}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <MDTypography fontSize={12}>Service Fee</MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography align="right" fontSize={12}>
                      {`${currency}${serviceCharge.toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <MDTypography fontSize={12}>VAT</MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography align="right" fontSize={12}>
                      {`${currency}${vatCharge.toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </MDTypography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <MDTypography fontSize={14} fontWeight="bold">
                      Total
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography align="right" fontSize={14} fontWeight="bold">
                      {`${currency}${totalCharge.toLocaleString("en", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              </Table>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenFundingDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              fundBalance();
            }}
          >
            Fund
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openWithdrawDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Withdraw Balance</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={2}>
              <MDInput
                name="title"
                margin="dense"
                label="Amount"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setFundingAmount(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenWithdrawDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="outlined"
            onClick={() => {
              withdrawBalance();
            }}
          >
            Withdraw
          </MDButton>
        </DialogActions>
      </Dialog>
      {pageLoaded && (
        <MDBox mb={5}>
          <MDBox mt={3} mb={1} display="flex" justifyContent="space-between">
            <MDBox>
              <Button
                color="dark"
                size="small"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    navigate(`/agent-benefits`);
                  }
                }}
              >
                Back
              </Button>
            </MDBox>
            <MDBox>
              <Button
                size="small"
                color="info"
                iconOnly
                onClick={() => {
                  setFundingAmount(0);
                  setOpenFundingDialog(true);
                }}
                startIcon={
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    add
                  </Icon>
                }
              >
                Fund
              </Button>
              &nbsp;
              <Button
                size="small"
                color="info"
                iconOnly
                onClick={() => {
                  setFundingAmount(0);
                  setOpenWithdrawDialog(true);
                }}
                startIcon={
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    remove
                  </Icon>
                }
              >
                Withdraw
              </Button>
              &nbsp;
              <Button
                size="small"
                color="info"
                iconOnly
                onClick={() => {
                  updateStatus();
                }}
                startIcon={
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    {!benefit.status ? "play_arrow" : "pause"}
                  </Icon>
                }
              >
                {!benefit.status ? "Enable" : "Disable"}
              </Button>
              &nbsp;
              <Button
                size="small"
                color="error"
                iconOnly
                onClick={() => {
                  if (window.confirm(`Terminate this benefit?`) === true) {
                    deleteBenefit();
                  }
                }}
                startIcon={
                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                    delete
                  </Icon>
                }
              >
                Terminate
              </Button>
            </MDBox>
          </MDBox>
          <MDBox mt={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox>
                      <MDTypography variant="h6">Benefit Information</MDTypography>
                    </MDBox>
                    <MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="regular">
                          {benefit.title}
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={3} p={1} display="flex" justifyContent="space-between">
                        <MDBox align="center">
                          <MDTypography variant="h6" fontWeight="light">
                            <Icon>money</Icon>
                            <br />
                            {benefit.benefit_type}
                          </MDTypography>
                        </MDBox>
                        <MDBox align="center">
                          <MDTypography variant="h6" fontWeight="light">
                            <Icon>event_repeat</Icon>
                            <br />
                            {benefit.cycle_period} days
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox align="center" p={3}>
                    <MDBox mt={1} mb={3}>
                      <MDTypography mb={1} color="success">
                        {currency}
                      </MDTypography>
                      <MDTypography variant="h6" fontSize={24}>{`${(
                        benefit.balance / 100
                      ).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}`}</MDTypography>
                    </MDBox>
                    <MDBox mt={2}>
                      <MDTypography variant="h6" fontWeight="light">
                        Balance
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox align="center" p={3}>
                    <MDBox mt={1} mb={3}>
                      <MDTypography mb={1} color="error">
                        {currency}
                      </MDTypography>
                      <MDTypography variant="h6" fontSize={24}>{`${(spend / 100).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }
                      )}`}</MDTypography>
                    </MDBox>
                    <MDBox mt={2}>
                      <MDTypography variant="h6" fontWeight="light">
                        Total Spend
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
          <MDBox mt={5}>
            {groups.length > 0 ? (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox>
                    <MDBox p={3}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="h6" fontWeight="light">
                          <b>Agent Groups</b>
                          <br />
                          <small>Add agent groups to this benefit.</small>
                        </MDTypography>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            setSelectedAgentGroup(null);
                            setGroupbenefit(null);
                            setOpenNewAgentDialog(true);
                          }}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox>
                      <DataTable
                        table={{ columns: groupsTableColumns, rows: groups }}
                        isSorted
                        entriesPerPage={false}
                        showTotalEntries
                        canSearch={false}
                        buttons
                        noEndBorder
                      />
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            ) : (
              <MDBox>
                <Card sx={{ height: "100%", marginTop: "0px" }}>
                  <MDBox p={3}>
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6" fontSize={48}>
                          <Icon>groups</Icon>
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light" fontSize={16}>
                          To manage benefits, you must organise your agents in groups and add groups
                          to this benefit.
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={5} mb={3}>
                        <MDButton
                          variant="contained"
                          color="dark"
                          onClick={() => {
                            setSelectedAgentGroup(null);
                            setGroupbenefit(null);
                            setOpenNewAgentDialog(true);
                          }}
                          startIcon={<Icon>add</Icon>}
                        >
                          Add Group
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}
      <Footer />
    </DashboardLayout>
  );
}

export default AgentBenefitViewPage;
